import { useStepDocument } from '@/hooks/useStepDocument';
import { WarningCircle } from '@phosphor-icons/react';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';

interface ImportWarningsPanelProps {
  /** Id of the project's STEP document, or `null` while no STEP has been uploaded. */
  modelId: string | null;
}

/**
 * The non-fatal problems the STEP import reported, listed once per project.
 *
 * The backend already phrases each warning as a full sentence, so they are rendered verbatim
 * rather than translated. Nothing is rendered while the document loads, when it fails to load,
 * or when it carries no warnings — unlike `apt_generation_error` these never gate the page.
 */
export const ImportWarningsPanel: FC<ImportWarningsPanelProps> = ({ modelId }) => {
  const { t } = useTranslation();
  const { stepDocument } = useStepDocument(modelId);
  const warnings = stepDocument?.warnings;

  if (!warnings || warnings.length === 0) {
    return null;
  }

  return (
    <section className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4">
      <WarningCircle size={20} className="mt-0.5 shrink-0 text-amber-600" aria-hidden={true} />
      <div className="flex min-w-0 flex-col gap-1">
        <h2 className="m-0 text-sm font-semibold text-amber-700">{t('projectDetail.importWarningsTitle')}</h2>
        <ul className="m-0 flex list-disc flex-col gap-1 pl-4">
          {warnings.map((warning) => (
            <li key={warning} className="text-sm text-amber-700 [text-wrap:pretty]">
              {warning}
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
};
